import React, { useState } from 'react';
import {
  FolderIcon,
  StarIcon,
  ChartBarIcon,
  LightBulbIcon,
  BookOpenIcon,
  MagnifyingGlassIcon,
  ArrowLeftOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  UserCircleIcon,
  CreditCardIcon,
} from '@heroicons/react/24/outline';
import { SparklesIcon } from '@heroicons/react/24/solid';
import { useAuth } from '../context/AuthContext';
import GlobalSearch from './GlobalSearch';

const navigation = [
  { name: 'Models', href: '/models', icon: FolderIcon },
  { name: 'Power List™', href: '/rankings/power-list', icon: StarIcon },
  { name: 'Analyze', href: '/analyze', icon: ChartBarIcon },
  { name: 'Research', href: '/research', icon: MagnifyingGlassIcon },
  { name: 'Learn', href: '/learn', icon: BookOpenIcon },
  { name: 'Insights', href: '/insights', icon: LightBulbIcon },
  { name: 'Chat', href: '/chat', icon: SparklesIcon, special: true },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

function SubscriptionPill({ userRecord }) {
  if (!userRecord) return null;
  const tier = userRecord.subscription_tier;
  if (tier === 'trial') {
    const days = userRecord.trial_ends_at
      ? Math.max(0, Math.ceil((new Date(userRecord.trial_ends_at) - new Date()) / 86400000))
      : 0;
    return (
      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
        days <= 7 ? 'bg-red-500 bg-opacity-20 text-red-400' : 'bg-accent-100 bg-opacity-15 text-accent-100'
      }`}>
        {days}d trial
      </span>
    );
  }
  if (tier === 'quarterly') return <span className="text-xs px-2 py-0.5 rounded-full bg-primary-100 bg-opacity-20 text-primary-100 font-medium">Quarterly Plan</span>;
  if (tier === 'annual') return <span className="text-xs px-2 py-0.5 rounded-full bg-signal-positive bg-opacity-20 text-signal-positive font-medium">Annual Plan</span>;
  return null;
}


export default function Sidebar() {
  const { user, userRecord, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [avatarMenuOpen, setAvatarMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    window.location.href = '/login';
  };

  const currentPath = window.location.pathname;

  const initials = [userRecord?.data?.first_name, userRecord?.data?.last_name]
    .filter(Boolean)
    .map((n) => n[0].toUpperCase())
    .join('') || user?.email?.[0]?.toUpperCase() || '?';

  return (
    <header className="bg-bg-100 border-b border-bg-300 shadow-sm relative z-40">
      {/* Desktop nav (md+) */}
      <div className="hidden md:flex h-14 items-center px-4 sm:px-6 lg:px-8 w-full">
        <a href="/models" className="flex items-center mr-8">
          <img src="/logo_wordmark.svg" alt="SharpeMetrix" className="h-7" />
        </a>

        <nav className="flex space-x-2 lg:space-x-6">
          {navigation.map((item) => (
            <a
              key={item.name}
              href={item.href}
              className={classNames(
                currentPath === item.href
                  ? 'text-primary-100 border-b-2 border-primary-100'
                  : 'text-text-200 hover:text-text-100 hover:border-primary-100',
                'group relative flex items-center gap-x-2 px-2 py-1 text-base font-medium border-b-2 border-transparent transition-colors duration-150',
                item.special && 'animate-pulse hover:animate-none'
              )}
              title={item.name}
            >
              <item.icon className={classNames("h-5 w-5", item.special && "text-primary-100")} aria-hidden="true" />
              <span className="hidden lg:block">{item.name}</span>
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none lg:hidden whitespace-nowrap z-50">
                {item.name}
              </div>
            </a>
          ))}
        </nav>

        <div className="flex-1" />

        <div className="flex items-center mr-2 lg:mr-6">
          <div className="w-60 lg:w-80">
            <GlobalSearch />
          </div>
        </div>

        {/* Avatar dropdown */}
        <div className="relative">
          <button
            onClick={() => setAvatarMenuOpen((v) => !v)}
            className="flex items-center gap-2 rounded-lg px-2 py-1 hover:bg-bg-200 transition-colors group"
          >
            <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center text-white font-bold text-sm shrink-0">
              {initials}
            </div>
            <SubscriptionPill userRecord={userRecord} />
          </button>

          {avatarMenuOpen && (
            <>
              <div className="fixed inset-0 z-30" onClick={() => setAvatarMenuOpen(false)} />
              <div className="absolute right-0 mt-2 w-56 rounded-xl bg-bg-200 border border-bg-300 shadow-xl z-40 overflow-hidden">
                <div className="px-4 py-3 border-b border-bg-300">
                  <p className="text-sm font-medium text-text-100 truncate">
                    {userRecord?.data?.first_name
                      ? `${userRecord.data.first_name} ${userRecord.data.last_name || ''}`.trim()
                      : user?.email}
                  </p>
                  <p className="text-xs text-text-300 truncate mt-0.5">{user?.email}</p>
                </div>
                <div className="py-1">
                  <a
                    href="/profile"
                    onClick={() => setAvatarMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-text-200 hover:bg-bg-300 hover:text-text-100 transition-colors"
                  >
                    <UserCircleIcon className="w-4 h-4" /> Profile & Settings
                  </a>
                  <a
                    href="/profile"
                    onClick={() => setAvatarMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-text-200 hover:bg-bg-300 hover:text-text-100 transition-colors"
                  >
                    <CreditCardIcon className="w-4 h-4" />
                    <span className="flex-1">Subscription</span>
                    <SubscriptionPill userRecord={userRecord} />
                  </a>
                </div>
                <div className="border-t border-bg-300 py-1">
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-text-200 hover:bg-bg-300 hover:text-red-400 transition-colors"
                  >
                    <ArrowLeftOnRectangleIcon className="w-4 h-4" /> Sign out
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Mobile nav (< md) */}
      <div className="flex md:hidden h-14 items-center justify-between px-4">
        <a href="/models" className="flex items-center">
          <img src="/logo_wordmark.svg" alt="SharpeMetrix" className="h-6" />
        </a>
        <div className="flex items-center gap-2">
          <GlobalSearch compact />
          <button
            onClick={() => setMobileMenuOpen(true)}
            className="p-2 rounded-lg text-text-300 hover:text-text-100 hover:bg-bg-200 transition-colors"
          >
            <Bars3Icon className="h-6 w-6" />
          </button>
        </div>
      </div>

      {/* Mobile slide-out menu */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-black/60" onClick={() => setMobileMenuOpen(false)} />
          <div className="absolute right-0 top-0 h-full w-72 bg-bg-100 border-l border-bg-300 shadow-2xl flex flex-col">
            <div className="flex items-center justify-between px-4 h-14 border-b border-bg-300">
              <span className="text-sm font-medium text-text-200">Menu</span>
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="p-2 rounded-lg text-text-300 hover:text-text-100 hover:bg-bg-200 transition-colors"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            {/* User info with profile link */}
            <a
              href="/profile"
              onClick={() => setMobileMenuOpen(false)}
              className="px-4 py-4 border-b border-bg-300 hover:bg-bg-200 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center text-white font-bold text-lg shrink-0">
                  {initials}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-text-100 truncate">
                    {userRecord?.data?.first_name
                      ? `${userRecord.data.first_name} ${userRecord.data.last_name || ''}`.trim()
                      : user?.email}
                  </p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <p className="text-xs text-text-300 truncate">{user?.email}</p>
                    <SubscriptionPill userRecord={userRecord} />
                  </div>
                </div>
              </div>
            </a>

            <nav className="flex-1 overflow-y-auto py-2">
              {navigation.map((item) => (
                <a
                  key={item.name}
                  href={item.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={classNames(
                    currentPath === item.href
                      ? 'bg-bg-200 text-primary-100 border-l-2 border-primary-100'
                      : 'text-text-200 hover:bg-bg-200 hover:text-text-100 border-l-2 border-transparent',
                    'flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors'
                  )}
                >
                  <item.icon className={classNames("h-5 w-5", item.special && "text-primary-100")} aria-hidden="true" />
                  {item.name}
                </a>
              ))}

              <a
                href="/profile"
                onClick={() => setMobileMenuOpen(false)}
                className={classNames(
                  currentPath === '/profile'
                    ? 'bg-bg-200 text-primary-100 border-l-2 border-primary-100'
                    : 'text-text-200 hover:bg-bg-200 hover:text-text-100 border-l-2 border-transparent',
                  'flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors'
                )}
              >
                <UserCircleIcon className="h-5 w-5" /> Profile & Settings
              </a>
            </nav>

            <div className="border-t border-bg-300 p-4">
              <button
                onClick={handleLogout}
                className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-text-200 hover:bg-bg-200 hover:text-red-400 transition-colors"
              >
                <ArrowLeftOnRectangleIcon className="h-5 w-5" />
                Sign out
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
